import { useMemo } from 'react';
import { FormikProps, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import sportsApi from '@app/utils/sportsApi';
import { updateUserPreferences } from '@app/utils/updateUserPreferences';
import { DEFAULT_LANGUAGE, SupportedLanguage } from '@app/i18n/constants';

export type FormValues = {
    placement: string | null;
    date: string | null;
    language: SupportedLanguage | null;
};

export type RegionFormRef = FormikProps<FormValues>;

export const useRegionForm = (user, changeDelimiter, sendNotification) => {
    const { t, i18n } = useTranslation('settings');
    const currentDateFormat =
        localStorage.getItem('mvc-date-locale') || 'en-US';
    const { csvDelimiter: currentDelimiter } = user;
    const currentLanguage = (i18n.language ||
        DEFAULT_LANGUAGE) as SupportedLanguage;

    const validate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.placement) {
            errors.placement = t(
                'userSettings.region.messages.delimiterRequired',
            );
        }
        if (!values.date) {
            errors.date = t('userSettings.region.messages.dateFormatRequired');
        }

        return errors;
    };

    const initialValues: FormValues = {
        placement: currentDelimiter || null,
        date: currentDateFormat || null,
        language: currentLanguage || null,
    };

    const formik = useFormik({
        initialValues,
        onSubmit: async ({ placement, date, language }) => {
            try {
                if (placement !== currentDelimiter) {
                    const { id } = user;
                    const payload = {
                        domainObject: {
                            csvDelimiter: placement,
                        },
                    };
                    await sportsApi
                        .url(`/users/${id}`)
                        .json(payload)
                        .put()
                        .json();

                    await changeDelimiter(placement);
                    sendNotification(
                        t('userSettings.region.messages.delimiterSuccess'),
                    );
                }

                if (date !== currentDateFormat) {
                    localStorage.setItem('mvc-date-locale', date);
                    sendNotification(
                        t('userSettings.region.messages.dateFormatSuccess'),
                    );
                }

                if (language && language !== currentLanguage) {
                    const { id } = user;
                    await updateUserPreferences({
                        userId: id,
                        preferences: { language },
                    });
                    i18n.changeLanguage(language);
                    sendNotification(
                        t('userSettings.region.messages.languageSuccess'),
                    );
                }
            } catch (e) {
                sendNotification(t('userSettings.region.messages.updateError'));
            }
        },
        validate,
    });

    const clearAllFields = () => {
        formik.resetForm();
    };

    const saveRegionalSettings = () => {
        formik.submitForm();
    };

    const isSaveDisabled = useMemo(() => {
        return (
            !formik.values.date ||
            !formik.values.placement ||
            formik.isSubmitting
        );
    }, [formik.values]);

    return {
        formik,
        clearAllFields,
        saveRegionalSettings,
        isSaveDisabled,
    };
};
